import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { type BusinessLocationView } from '../../../utils/locationFilters';
import {
  LOCATION_VIEW_ICON_DEFINITIONS,
  DEFAULT_LOCATION_VIEW_ICON,
  renderLocationViewIcon,
} from '../../../constants/locationViewIcons';

const EMPTY_VIEW: Omit<BusinessLocationView, 'id'> = {
  label: '',
  sortOrder: 0,
  showInWeb: true,
  showInMobile: true,
  icon: DEFAULT_LOCATION_VIEW_ICON,
  filters: {},
};

const TRI_STATE_OPTIONS = [
  { value: 'all', label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const LocationViewsSettings: React.FC = () => {
  const [views, setViews] = useState<BusinessLocationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState<BusinessLocationView | null>(null);
  const [form, setForm] = useState<Omit<BusinessLocationView, 'id'>>(EMPTY_VIEW);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const businessId = localStorage.getItem('businessId');

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    if (!businessId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}/location-views`, { headers: authHeaders() });
        if (!cancelled) {
          setViews(Array.isArray(res.data.location_views) ? res.data.location_views : []);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.message ?? 'Failed to load location views');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const saveViews = async (updated: BusinessLocationView[]) => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/location-views`,
        { location_views: updated },
        { headers: authHeaders() }
      );
      const saved: BusinessLocationView[] = res.data.location_views ?? updated;
      setViews(saved);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      window.dispatchEvent(new Event('locationViewsChanged'));
      return saved;
    } catch (e: any) {
      setError(e.response?.data?.message ?? 'Failed to save location views');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setEditingView(null);
    setForm({ ...EMPTY_VIEW, sortOrder: views.length });
    setDialogOpen(true);
  };

  const openEdit = (view: BusinessLocationView) => {
    setEditingView(view);
    setForm({
      label: view.label,
      sortOrder: view.sortOrder,
      showInWeb: view.showInWeb,
      showInMobile: view.showInMobile,
      icon: view.icon ?? DEFAULT_LOCATION_VIEW_ICON,
      filters: { ...view.filters },
    });
    setDialogOpen(true);
  };

  const handleSaveDialog = async () => {
    if (!form.label.trim()) return;
    let updated: BusinessLocationView[];
    if (editingView) {
      updated = views.map(v => v.id === editingView.id ? { ...editingView, ...form } : v);
    } else {
      updated = [...views, { id: crypto.randomUUID(), ...form }];
    }
    const saved = await saveViews(updated);
    if (saved !== null) setDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    const updated = views.filter(v => v.id !== id);
    await saveViews(updated);
    setDeleteId(null);
  };

  const setFilter = (key: string, value: string | undefined) => {
    setForm(prev => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
    }));
  };

  const f = form.filters;

  const filterSummaryFor = (view: BusinessLocationView): string[] => {
    const summary: string[] = [];
    if (view.filters.searchQuery) summary.push(`Search: "${view.filters.searchQuery}"`);
    if (view.filters.hasCoordinates && view.filters.hasCoordinates !== 'all') {
      summary.push(`Coordinates: ${view.filters.hasCoordinates === 'true' ? 'yes' : 'no'}`);
    }
    if (view.filters.hasContact && view.filters.hasContact !== 'all') {
      summary.push(`Contact: ${view.filters.hasContact === 'true' ? 'yes' : 'no'}`);
    }
    if (view.filters.hasAddress && view.filters.hasAddress !== 'all') {
      summary.push(`Address: ${view.filters.hasAddress === 'true' ? 'yes' : 'no'}`);
    }
    return summary;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Location Views</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create custom named views that appear as sidebar shortcuts in the web admin and
        quick-access buttons in the mobile app. Each view saves a set of location filters
        that is applied automatically when clicked.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Location views saved.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={saving}>
          Add View
        </Button>
      </Box>

      {views.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
          <Typography variant="body2" color="text.secondary">
            No custom views yet. Add one to create a bespoke sidebar shortcut.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>Label</TableCell>
                <TableCell>Filters</TableCell>
                <TableCell align="center">Web</TableCell>
                <TableCell align="center">Mobile</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {views.map(view => {
                const filterSummary = filterSummaryFor(view);
                return (
                  <TableRow key={view.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                        {renderLocationViewIcon(view.icon, 'small')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{view.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {filterSummary.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">None</Typography>
                        ) : filterSummary.map(s => (
                          <Chip key={s} label={s} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{view.showInWeb ? '✓' : '–'}</TableCell>
                    <TableCell align="center">{view.showInMobile ? '✓' : '–'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Preview in locations list">
                        <IconButton size="small" onClick={() => window.open(`/admin/location-views/${view.id}`, '_blank')}>
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => openEdit(view)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteId(view.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingView ? 'Edit View' : 'Add Location View'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'visible' }}>
          <TextField
            label="View label"
            value={form.label}
            onChange={e => setForm(p => ({ ...p, label: e.target.value.slice(0, 40) }))}
            required
            fullWidth
            margin="normal"
            helperText="Shown in the sidebar and mobile app (max 40 chars)"
            inputProps={{ maxLength: 40 }}
            autoFocus
            sx={{
              mt: 0.5,
              '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                bgcolor: 'background.paper',
                px: 0.5,
              },
            }}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Icon</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                gap: 1,
              }}
            >
              {LOCATION_VIEW_ICON_DEFINITIONS.map(({ key, label }) => {
                const selected = (form.icon ?? DEFAULT_LOCATION_VIEW_ICON) === key;
                return (
                  <Tooltip key={key} title={label}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setForm(p => ({ ...p, icon: key }))}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 1,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {renderLocationViewIcon(key, 'small')}
                      <Typography variant="caption" noWrap sx={{ maxWidth: '100%', fontSize: '0.65rem' }}>
                        {label}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          <Typography variant="subtitle2">Saved filters</Typography>

          <TextField
            label="Default search"
            value={f.searchQuery ?? ''}
            onChange={e => setFilter('searchQuery', e.target.value.slice(0, 200) || undefined)}
            fullWidth
            helperText="Optional text filter applied when the view opens"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Has coordinates</InputLabel>
            <Select
              label="Has coordinates"
              value={f.hasCoordinates ?? 'all'}
              onChange={e => setFilter('hasCoordinates', e.target.value === 'all' ? undefined : e.target.value)}
            >
              {TRI_STATE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Has contact info</InputLabel>
            <Select
              label="Has contact info"
              value={f.hasContact ?? 'all'}
              onChange={e => setFilter('hasContact', e.target.value === 'all' ? undefined : e.target.value)}
            >
              {TRI_STATE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Has address</InputLabel>
            <Select
              label="Has address"
              value={f.hasAddress ?? 'all'}
              onChange={e => setFilter('hasAddress', e.target.value === 'all' ? undefined : e.target.value)}
            >
              {TRI_STATE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={form.showInWeb} onChange={e => setForm(p => ({ ...p, showInWeb: e.target.checked }))} />}
            label="Show in web admin sidebar"
          />
          <FormControlLabel
            control={<Switch checked={form.showInMobile} onChange={e => setForm(p => ({ ...p, showInMobile: e.target.checked }))} />}
            label="Show in mobile app (More → Locations)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDialog} disabled={!form.label.trim() || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete view?</DialogTitle>
        <DialogContent>
          <Typography>This will remove the view from the sidebar and mobile shortcuts.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteId && handleDelete(deleteId)} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationViewsSettings;
